import { uiText } from "@/lib/i18n/uiText";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { captureError, sessionRequestId } from "@/lib/sentry";
import { isStaleChunkError, reloadForStaleChunk } from "@/lib/staleChunkRecovery";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback renderer. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** When this changes (e.g. the page address), a shown error is cleared. */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * App-wide error boundary. A render-time exception anywhere in the tree would
 * otherwise white-screen the entire SPA with no recovery path. This catches it,
 * logs it, reports it (consent-gated) and shows a branded, actionable fallback
 * so the user can recover without a hard refresh.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  private eventId: string | undefined;

  override componentDidUpdate(prev: ErrorBoundaryProps) {
    // Without this, one broken page kept every later page blank until refresh.
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[Statlign] Uncaught render error:", error, info.componentStack);
    if (isStaleChunkError(error) && reloadForStaleChunk()) return;
    try {
      this.eventId = captureError(error, {
        component_stack: info.componentStack?.slice(0, 2000),
        boundary: "app-root",
      });
    } catch {
      // Never let error reporting throw inside the boundary.
    }
    try {
      trackEvent("client_error", {
        message: error.message?.slice(0, 300),
        component_stack: info.componentStack?.slice(0, 500),
      });
    } catch {
      // Never let error reporting throw inside the boundary.
    }
  }

  private reset = () => {
    this.setState({ error: null });
  };

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="rounded-full bg-destructive/10 p-4">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">{uiText("Something went wrong")}</h1>
          <p className="max-w-md text-muted-foreground">
             {uiText("Statlign hit an unexpected error rendering this page. Your data is safe — try again or head back to the homepage.")} </p>
          <p className="text-xs text-muted-foreground/80">
             {uiText("Reference ID:")}{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono">
              {this.eventId || sessionRequestId()}
            </code>
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button onClick={this.reset}>{uiText("Try again")}</Button>
          <Button variant="outline" onClick={() => (window.location.href = "/")}>
             {uiText("Go to homepage")} </Button>
        </div>
      </div>
    );
  }
}
